/**
 * Rewrite land-trip Arrive/Depart from home-TZ absolute instants into UTC wall-clock.
 * Mirrors iOS SyncMigration.rewriteLandTripWallClockDates (#132).
 */

import type { Trip, TripCityVisitMeta } from '../types/client';
import { wallClockIsoFromSourceTz, ymdFromUnknown } from './wallClock';

function rewriteIsoField(
  value: string | null | undefined,
  sourceTimeZone: string
): string | null | undefined {
  if (value == null || !String(value).trim()) return value;
  // Already civil wall-clock (no offset math needed) if we only have YMD.
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) return value;
  return wallClockIsoFromSourceTz(value, sourceTimeZone) ?? value;
}

export function rewriteLandCityVisitWallClock(
  meta: TripCityVisitMeta,
  sourceTimeZone: string
): TripCityVisitMeta {
  const tz = sourceTimeZone.trim();
  if (!tz) return meta;
  return {
    ...meta,
    arrivalDate: rewriteIsoField(meta.arrivalDate, tz) ?? meta.arrivalDate,
    departureDate: rewriteIsoField(meta.departureDate, tz) ?? meta.departureDate,
  };
}

/**
 * Apply home-TZ → wall-clock rewrite to all city visits on a land trip.
 * Cruise catalog times are already UTC wall-clock — skip cruise trips.
 */
export function rewriteLandTripWallClockDates(
  trip: Trip,
  sourceTimeZone: string
): Trip {
  if (trip.tripType === 'cruise') return trip;
  const tz = sourceTimeZone.trim();
  if (!tz || !trip.cityVisitMeta) return trip;

  const nextMeta: Record<string, TripCityVisitMeta> = {};
  let changed = false;
  for (const [id, meta] of Object.entries(trip.cityVisitMeta)) {
    const rewritten = rewriteLandCityVisitWallClock(meta, tz);
    nextMeta[id] = rewritten;
    if (
      rewritten.arrivalDate !== meta.arrivalDate ||
      rewritten.departureDate !== meta.departureDate
    ) {
      changed = true;
    }
  }
  if (!changed) return trip;

  const arrivals = Object.values(nextMeta)
    .map((m) => ymdFromUnknown(m.arrivalDate))
    .filter((v): v is string => Boolean(v))
    .sort();
  return {
    ...trip,
    cityVisitMeta: nextMeta,
    startDate: arrivals[0] ?? trip.startDate,
    endDate: arrivals[arrivals.length - 1] ?? trip.endDate,
  };
}
